import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";

export function Footer() {
  return (
    <footer className="bg-primary text-white" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4 font-montserrat text-zinc-50">London Secondary Glazing</h3>
            <p className="mb-4 font-inter">
              Professional secondary glazing solutions for London properties. Improve insulation, reduce noise, and enhance your home's comfort.
            </p>
            <nav className="flex space-x-4 mt-6" aria-label="Social media links">
              <a href="https://www.facebook.com/londonsecondaryglazing" className="hover:text-gray-300" aria-label="Follow us on Facebook" rel="noopener noreferrer" target="_blank">
                <Facebook size={20} />
              </a>
              <a href="https://www.twitter.com/ldnsecondaryglazing" className="hover:text-gray-300" aria-label="Follow us on Twitter" rel="noopener noreferrer" target="_blank">
                <Twitter size={20} />
              </a>
              <a href="https://www.instagram.com/londonsecondaryglazing" className="hover:text-gray-300" aria-label="Follow us on Instagram" rel="noopener noreferrer" target="_blank">
                <Instagram size={20} />
              </a>
              <a href="https://www.linkedin.com/company/london-secondary-glazing" className="hover:text-gray-300" aria-label="Connect with us on LinkedIn" rel="noopener noreferrer" target="_blank">
                <Linkedin size={20} />
              </a>
            </nav>
          </div>
          
          <nav aria-label="Services navigation">
            <h3 className="text-xl font-bold mb-4 font-montserrat text-gray-50">Services</h3>
            <ul className="space-y-2 font-inter text-sm">
              <li><Link to="/secondary-glazing" className="hover:text-gray-300">Secondary Glazing</Link></li>
              <li><Link to="/noise-reduction-secondary-glazing" className="hover:text-gray-300">Noise Reduction</Link></li>
              <li><Link to="/thermal-insulation-secondary-glazing" className="hover:text-gray-300">Thermal Insulation</Link></li>
              <li><Link to="/heritage-preservation-secondary-glazing" className="hover:text-gray-300">Heritage Solutions</Link></li>
              <li><Link to="/secondary-glazing-benefits" className="hover:text-gray-300">Benefits</Link></li>
              <li><Link to="/secondary-glazing-cost" className="hover:text-gray-300">Cost Guide</Link></li>
            </ul>
          </nav>
          
          <nav aria-label="Window types navigation">
            <h3 className="text-xl font-bold mb-4 font-montserrat text-zinc-50">Window Types</h3>
            <ul className="space-y-2 font-inter text-sm">
              <li><Link to="/fixed-units-glazing" className="hover:text-gray-300">Fixed Units</Link></li>
              <li><Link to="/sliding-units-glazing" className="hover:text-gray-300">Sliding Units</Link></li>
              <li><Link to="/hinged-units-glazing" className="hover:text-gray-300">Hinged Units</Link></li>
              <li><Link to="/lift-out-units-glazing" className="hover:text-gray-300">Lift-Out Units</Link></li>
              <li><Link to="/horizontal-sliding-units-glazing" className="hover:text-gray-300">Horizontal Sliding</Link></li>
              <li><Link to="/vertical-sliding-units-glazing" className="hover:text-gray-300">Vertical Sliding</Link></li>
            </ul>
          </nav>
          
          <nav aria-label="Service areas navigation">
            <h3 className="text-xl font-bold mb-4 font-montserrat text-zinc-50">Popular Areas</h3>
            <ul className="space-y-2 font-inter text-sm">
              <li><Link to="/mayfair-secondary-glazing" className="hover:text-gray-300">Mayfair</Link></li>
              <li><Link to="/knightsbridge-secondary-glazing" className="hover:text-gray-300">Knightsbridge</Link></li>
              <li><Link to="/chelsea-secondary-glazing-guide" className="hover:text-gray-300">Chelsea</Link></li>
              <li><Link to="/kensington-secondary-glazing" className="hover:text-gray-300">Kensington</Link></li>
              <li><Link to="/hampstead-secondary-glazing" className="hover:text-gray-300">Hampstead</Link></li>
              <li><Link to="/locations" className="hover:text-gray-300">All Areas</Link></li>
            </ul>
          </nav>
          
          <address className="not-italic">
            <h3 className="text-xl font-bold mb-4 font-montserrat text-zinc-50">Contact & Info</h3>
            <ul className="space-y-3 font-inter text-sm">
              <li className="flex items-start">
                <MapPin className="mr-2 h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                  <span itemProp="streetAddress">Arch 141, MacFarlane Road</span>, 
                  <span itemProp="addressLocality"> London</span>, 
                  <span itemProp="postalCode">W12 7LA</span>
                </span>
              </li>
              <li className="flex items-center">
                <Phone className="mr-2 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <a href="tel:+442070601572" className="hover:text-gray-300" itemProp="telephone">+44 0207 060 1572</a>
              </li>
              <li className="flex items-center">
                <Mail className="mr-2 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <a href="mailto:info@londonsecondaryglazing.co.uk" className="hover:text-gray-300" itemProp="email">info@londonsecondaryglazing.co.uk</a>
              </li>
            </ul>
            <nav className="mt-4 space-y-2" aria-label="Quick links">
              <Link to="/get-quote" className="block">
                <Button className="bg-white text-primary hover:bg-gray-200 w-full text-sm">
                  Get Free Quote
                </Button>
              </Link>
              <div className="flex flex-wrap gap-2 text-xs">
                <Link to="/contact" className="hover:text-gray-300">Contact</Link>
                <Link to="/faq" className="hover:text-gray-300">FAQ</Link>
                <Link to="/blog-secondary-glazing" className="hover:text-gray-300">Blog</Link>
                <Link to="/sitemap" className="hover:text-gray-300">Sitemap</Link>
                <a href="https://secondarydoubleglazing.co.uk/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">Industry Resources</a>
              </div>
            </nav>
          </address>
        </div>
        
        <div className="border-t border-white/20 mt-10 pt-6 text-sm">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="mb-4 md:mb-0">© {new Date().getFullYear()} London Secondary Glazing. All rights reserved.</p>
            <nav className="flex space-x-4 font-inter" aria-label="Legal navigation">
              <Link to="/privacy-policy" className="hover:text-gray-300">Privacy Policy</Link>
              <Link to="/terms-conditions" className="hover:text-gray-300">Terms & Conditions</Link>
              <Link to="/cookie-policy" className="hover:text-gray-300">Cookie Policy</Link>
              <Link to="/accessibility-statement" className="hover:text-gray-300">Accessibility</Link>
              <Link to="/disclaimer" className="hover:text-gray-300">Disclaimer</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}